/**
 * Client-side API wrapper.
 * Thin layer over apiFetch with in-flight request deduplication.
 */

import { apiFetch, type Result, type FetchOptions, type FetchError } from "../../shared/api-fetch.js";
import { createLogger } from "./logger.js";

export type { Result, FetchOptions, FetchError };

const log = createLogger('api-client');

// In-flight request deduplication
const inFlight = new Map<string, Promise<Result<unknown>>>();
const SESSION_HEADERS = ["authorization", "cookie"];
// Design decision: DEDUP_INTERVAL
// - 200ms: Pure concurrent dedup. Only requests in the same render cycle share results.
// - 5000ms: Short-lived cache. Components mounting within 5s share the same result.
//           Reduces network calls but may serve slightly stale data.
// Choose based on data freshness requirements.
const DEDUP_INTERVAL = 5000;

/**
 * Generate cache key from URL + non-session headers.
 * Session headers (Authorization, Cookie) are excluded since they're
 * constant within a browser session and don't differentiate requests.
 *
 * Precondition: url is a valid string
 * Postcondition: Returns a unique cache key for the request
 */
function getCacheKey(url: string, headers?: HeadersInit): string {
  if (!headers) return url;

  let entries: [string, string][];
  if (headers instanceof Headers) {
    entries = [];
    headers.forEach((value, key) => entries.push([key, value]));
  } else {
    entries = Object.entries(headers) as [string, string][];
  }

  const filtered = entries
    .map(([k, v]) => [k.toLowerCase(), v] as const)
    .filter(([k]) => !SESSION_HEADERS.includes(k))
    .sort(([a], [b]) => a.localeCompare(b));

  return filtered.length ? `${url}::${JSON.stringify(filtered)}` : url;
}

/**
 * Client fetch wrapper with in-flight request deduplication.
 * Emits ft-fetch-error event on failure for optional global handling.
 * Components handle errors via Result type.
 *
 * Precondition: url is a valid API endpoint
 * Postcondition: Returns Result<T>, dedupes concurrent GET requests
 * Invariant: Only one network request per unique URL+headers at a time
 */
export async function clientFetch<T>(
  url: string,
  options?: FetchOptions,
): Promise<Result<T>> {
  const isGet = !options?.method || options.method === "GET";
  const key = getCacheKey(url, options?.headers);

  if (isGet && inFlight.has(key)) {
    log.debug('dedup hit', { url });
    return inFlight.get(key) as Promise<Result<T>>;
  }

  log.log('fetch', { url, method: options?.method || 'GET' });

  const promise = apiFetch<T>(url, options).then((result) => {
    if (!result.ok) {
      log.error('fetch failed', { url, error: result.error });

      // Redirect to rate limit page on 429, preserving original URL
      if (result.error.type === 'rate_limit') {
        const returnUrl = encodeURIComponent(window.location.href);
        window.location.href = `/rate-limit?return=${returnUrl}`;
        return result;
      }

      window.dispatchEvent(
        new CustomEvent<FetchError>("ft-fetch-error", { detail: result.error }),
      );
    } else {
      log.debug('fetch success', { url });
    }
    return result;
  });

  if (isGet) {
    inFlight.set(key, promise);
    promise.then((result) => {
      // Errors: clear immediately to allow retry
      // Success: keep for DEDUP_INTERVAL to catch near-simultaneous requests
      const delay = result.ok ? DEDUP_INTERVAL : 0;
      setTimeout(() => {
        if (inFlight.get(key) === promise) {
          inFlight.delete(key);
          log.debug('dedup cleared', { url });
        }
      }, delay);
    });
  }

  return promise;
}
